import { MessageKey } from "common/i18n/message-key";
import { confirmTokenParamName } from "common/utils/regex/tokens";

import { config } from "../config";
import { getUserPreferences } from "./user-preferences";

import type { ApiPayload } from "common/classes/api-payload";
import type { LanguagePreference, ThemePreference } from "common/types/user-preferences";

class ApiClient {
  private baseUrl: string;

  private csrfToken: string;

  constructor() {
    this.baseUrl = config.apiBaseUrl;
    this.csrfToken = ``;
  }

  setCsrfToken(token: string): void {
    this.csrfToken = token;
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<ApiPayload> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      "Content-Type": `application/json`,
      "Accept-Language": (() => {
        const pref = getUserPreferences().language;

        if (pref === `en` || pref === `es` || pref === `pt`) {
          return pref;
        }

        if (navigator.languages) {
          return navigator.languages.join(`,`);
        }

        return navigator.language;
      })(),
    };

    const csrfHeaders: Record<string, string> = this.csrfToken ? { "csrf-token": this.csrfToken } : {};

    const customHeaders = (options.headers as Record<string, string>) ?? {};

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...csrfHeaders,
          ...customHeaders,
        },
        credentials: `include`,
      });

      let responseData: ApiPayload;

      const contentType = response.headers.get(`content-type`);

      if (contentType?.includes(`application/json`)) {
        responseData = await response.json();
      } else {
        responseData = {
          success: false,
          message: MessageKey.internalServerError,
          details: {},
          traceId: `none-frontend-generated`,
        };
      }

      if (!response.ok) {
        if (responseData.message) {
          return responseData;
        }

        // Fall back to a stable error code that UI maps to a localized message
        throw new Error(`unexpectedError`);
      }

      return responseData;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes(`fetch`)) {
        // Use a stable error code that UI maps to i18n (translations.common.networkError)
        throw new Error(`networkError`);
      }

      if (error instanceof Error) {
        throw error;
      }

      // Generic, non-diagnostic error code for unexpected failures
      throw new Error(`unexpectedError`);
    }
  }

  async post(endpoint: string, data?: unknown, headers: Record<string, string> = {}): Promise<ApiPayload> {
    return this.request(endpoint, {
      method: `POST`,
      body: data ? JSON.stringify(data) : undefined,
      headers,
    });
  }

  async get(endpoint: string): Promise<ApiPayload> {
    return this.request(endpoint, {
      method: `GET`,
    });
  }

  async createAccount(email: string, password: string, firstName: string, lastName: string, turnstileToken?: string) {
    return this.post(`/api/create-account`, { email, password, firstName, lastName, turnstileToken });
  }

  async confirmAccount(email: string, confirmAccountToken: string) {
    return this.get(
      `/api/confirm-account?email=${encodeURIComponent(email)}&${confirmTokenParamName}=${encodeURIComponent(confirmAccountToken)}`,
    );
  }

  async resendConfirmationEmail(email: string) {
    return this.post(`/api/resend-confirmation-email`, { email });
  }

  async login(email: string, password: string, turnstileToken?: string): Promise<ApiPayload> {
    return this.post(`/api/login`, { email, password, turnstileToken });
  }

  async logout() {
    return this.post(`/api/account/logout`);
  }

  async accountData() {
    return this.get(`/api/account/data`);
  }

  async forgotPassword(email: string, turnstileToken?: string) {
    return this.post(`/api/forgot-password`, { email, turnstileToken });
  }

  async resetPassword(email: string, resetToken: string, newPassword: string) {
    return this.post(`/api/reset-password`, { email, resetToken, newPassword });
  }

  async changePassword(oldPassword: string, newPassword: string) {
    return this.post(`/api/account/change-password`, { oldPassword, newPassword });
  }

  async updateProfile(data: { firstName?: string; lastName?: string }): Promise<ApiPayload> {
    const body: Record<string, string> = {};

    if (data.firstName) {
      body.firstName = data.firstName;
    }

    if (data.lastName) {
      body.lastName = data.lastName;
    }

    return this.post(`/api/account/profile`, body);
  }

  async updatePreferences(prefs: {
    language?: LanguagePreference | `auto`;
    theme?: ThemePreference;
  }): Promise<ApiPayload> {
    const body: Record<string, string> = {};

    const allowedLanguages: Array<LanguagePreference> = [`en`, `es`, `pt`];

    if (prefs.language && allowedLanguages.includes(prefs.language as LanguagePreference)) {
      body.language = prefs.language as LanguagePreference;
    }

    const allowedThemes: Array<ThemePreference> = [`light`, `dark`, `auto`];

    if (prefs.theme && allowedThemes.includes(prefs.theme)) {
      body.theme = prefs.theme;
    }

    return this.post(`/api/account/preferences`, body);
  }

  async getPreferences() {
    return this.get(`/api/account/preferences`);
  }

  async fetchCsrfToken(): Promise<{ csrfToken: string }> {
    const response = await fetch(`${this.baseUrl}/csrf-token`, { credentials: `include` });

    const data = (await response.json()) as { csrfToken: string };

    return data;
  }
}

export const apiClient = new ApiClient();
